<template>
  <div class="flex flex-col items-center justify-center print:flex-row print:gap-4">
    <div class="flex h-full w-full flex-col items-stretch justify-center print:h-auto print:w-1/2">
      <div
        v-if="skillAnalysis.length > 0"
        class="relative z-0 flex h-full flex-col items-center justify-center"
      >
        <div class="flex w-full grow items-center justify-center p-2 print:grow-0">
          <div
            ref="container"
            class="relative aspect-square w-full max-w-110 rounded-full shadow-md print:shadow-none"
            :style="fixWidth ? { width: fixWidth + 'px' } : {}"
          >
            <svg
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              :width="chartWidth"
              :height="chartWidth"
              class="absolute top-0 left-0 z-10 origin-center"
            >
              <MenonChartSkillSegment
                v-for="(segment, index) in chartSegments"
                :key="segment.id"
                :max-value="maxValue"
                :color="segment.skill.color"
                :value="animated ? segment.value : null"
                :value-shade="segment.valueShade"
                :all-perspectives="topicPerspectives"
                :perspective="segment.perspectiveKey"
                :width="chartWidth"
                :index="index"
                :total-number-of-segments="chartSegments.length"
                :hovered="hoveredSkillId == segment.skill.id"
              />
              <MenonChartGrid
                :max-value="maxValue"
                :width="chartWidth"
                :number-of-segments="chartSegments.length"
                :background-color="backgroundColor"
              />
              <MenonChartSkillSegmentMouseTarget
                v-for="(segment, index) in chartSegments"
                :key="segment.id"
                :skill="segment.skill"
                :width="chartWidth"
                :number-of-segments="chartSegments.length"
                :index="index"
                :max-value="maxValue"
                :topic-perspectives="topicPerspectives ?? []"
                :perspective-key="segment.perspectiveKey ?? undefined"
                :perspective-averages="segment.perspectiveAverages"
                :perspectives="perspectives"
                :hovered="hoveredSkillId == segment.skill.id"
                :split-by-perspective="splitByPerspective ?? false"
                @click="selected(segment.skill.id)"
                @over="() => onMouseIn(segment.skill.id)"
                @out="() => onMouseOut()"
              />
            </svg>
          </div>
        </div>
      </div>
      <MenonChartLegend
        :perspectives="perspectives"
        :all-perspectives="topicPerspectives"
        :active="hoveredSkillId != null"
        :color="hoveredSkill?.color"
        :split-by-perspective="splitByPerspective ?? false"
      />
    </div>
    <div
      class="flex flex-row flex-wrap items-center justify-center gap-1 print:w-1/2 print:flex-col print:flex-nowrap print:items-start"
    >
      <div
        v-for="skill in skills"
        :key="skill.id"
        class="rounded-small group text-label-large relative flex cursor-pointer flex-row items-center justify-center overflow-hidden border px-2 py-1 whitespace-nowrap select-none print:min-h-auto! print:border-none! print:bg-transparent! print:p-0! print:whitespace-normal"
        :class="[
          hoveredSkill?.id == skill.id &&
            'text-' + skill.color + '-40 border-' + skill.color + '-40 bg-' + skill.color + '-95',
          hoveredSkillId != skill.id &&
            'text-' + skill.color + '-60 border-' + skill.color + '-90 bg-' + skill.color + '-99',
        ]"
        @mouseenter="() => onMouseIn(skill.id)"
        @mouseleave="() => onMouseOut()"
        @click="selected(skill.id)"
      >
        {{ skill.title }}
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { compareSkills } from '@/core/models/skill'
  import { useElementSize } from '@vueuse/core'
  import MenonChartSkillSegmentMouseTarget from './components/MenonChartSkillSegmentMouseTarget.vue'
  import MenonChartGrid from './components/MenonChartGrid.vue'
  import MenonChartSkillSegment from './components/MenonChartSkillSegment.vue'
  import MenonChartLegend from './components/MenonChartLegend.vue'
  import type { SkillAnalysis } from '@/core/models/surveys-analytics'
  import { analysisPerspectiveKey } from '@/core/models/perspective-analysis'
  import { SELF_PERSPECTIVE_KEY, type Perspective } from '@/core/models/perspective'

  interface Props {
    maxValue: number
    skillAnalysis: (SkillAnalysis & {
      average: number | null
    })[]
    topicPerspectives?: ReadonlyArray<Perspective>
    /** Draw one wedge per perspective inside each skill, instead of one averaged wedge per skill. */
    splitByPerspective?: boolean
    fixWidth?: number
    backgroundColor?:
      | 'surface-container-low'
      | 'surface-container-high'
      | 'surface-container-highest'
      | 'surface-container-lowest'
      | 'surface-container'
      | 'surface'
  }

  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'skillSelected', skillId: string): void
  }>()

  const VALUE_SHADES = ['40', '60', '70', '80', '50'] as const

  const perspectiveKeys = computed(() => {
    const present = new Set<string>()
    for (const section of props.skillAnalysis) {
      for (const entry of section.perspectives) {
        const key = analysisPerspectiveKey(entry)
        if (entry.average != null && key) {
          present.add(key)
        }
      }
    }

    const ordered: string[] = []
    if (present.has(SELF_PERSPECTIVE_KEY)) {
      ordered.push(SELF_PERSPECTIVE_KEY)
    }
    for (const perspective of props.topicPerspectives ?? []) {
      if (
        perspective.key !== SELF_PERSPECTIVE_KEY &&
        present.has(perspective.key) &&
        !ordered.includes(perspective.key)
      ) {
        ordered.push(perspective.key)
      }
    }
    for (const key of present) {
      if (!ordered.includes(key)) {
        ordered.push(key)
      }
    }
    return ordered
  })

  const perspectives = computed(() => new Set(perspectiveKeys.value))

  const usesPerspectiveSegments = computed(
    () => !!props.splitByPerspective && perspectiveKeys.value.length > 0,
  )

  const sortedSections = computed(() => {
    return [...props.skillAnalysis].sort((a, b) => compareSkills(a.skill, b.skill))
  })

  const perspectiveAveragesFor = (section: SkillAnalysis & { average: number | null }) => {
    return section.perspectives.flatMap((p) => {
      const key = analysisPerspectiveKey(p)
      return key ? [{ key, average: p.average }] : []
    })
  }

  const chartSegments = computed(() => {
    if (!usesPerspectiveSegments.value) {
      return sortedSections.value.map((section) => ({
        id: section.skill.id,
        skill: section.skill,
        value: section.average,
        perspectiveKey: null as string | null,
        valueShade: '60',
        perspectiveAverages: perspectiveAveragesFor(section),
      }))
    }

    const keys = perspectiveKeys.value
    return sortedSections.value.flatMap((section) =>
      keys.map((key, perspectiveIndex) => {
        const entry = section.perspectives.find((p) => analysisPerspectiveKey(p) === key)
        return {
          id: `${section.skill.id}:${key}`,
          skill: section.skill,
          value: entry?.average ?? null,
          perspectiveKey: key,
          valueShade:
            keys.length <= 1 ? '60' : VALUE_SHADES[perspectiveIndex % VALUE_SHADES.length]!,
          perspectiveAverages: perspectiveAveragesFor(section),
        }
      }),
    )
  })

  const hoveredSkillId = ref<string | null>(null)

  const skills = computed(() => {
    return props.skillAnalysis.flatMap((section) => section.skill)
  })

  const hoveredSkill = computed(() => {
    return skills.value.find((skill) => skill.id == hoveredSkillId.value)
  })

  const selected = (skillId: string) => {
    emits('skillSelected', skillId)
  }

  const onMouseIn = (skillId: string) => {
    hoveredSkillId.value = skillId
  }
  const onMouseOut = () => {
    hoveredSkillId.value = null
  }

  const container = ref<HTMLButtonElement | null>(null)
  const { width } = useElementSize(container)

  const chartWidth = computed(() => {
    return props.fixWidth ?? width.value
  })

  const animated = ref(false)

  onMounted(() => {
    setTimeout(() => {
      animated.value = true
    }, 10)
  })
</script>
